import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronRight, FiPlus, FiX } from 'react-icons/fi';
import { clauseSummary, querySummary, queryTables } from '../../utils/sqlStructure/summarize';
import { ADDABLE_CLAUSES } from '../../utils/sqlStructure/edits';

/**
 * SQL 빌더 왼쪽 — 「축약된」 SQL 요소(질의 블록 아웃라인, 2026-10-19 사용자 결정: 기타형).
 * 질의마다 카드 한 장, 카드 안의 절은 한 줄 요약, 서브쿼리는 그 절 아래 들여쓴 카드. 그리기만 한다 —
 * 추가·삭제는 onAction 으로 올려 보내고 빌더가 원문을 고친 뒤 다시 파싱한다(이 컴포넌트는 상태를 들지 않는다, 펼침만 빼고).
 */

/** 같은 절 안에서 같은 종류의 몇 번째인지(이름 없는 서브쿼리의 표시) */
const ordinalOf = (q) => {
  const m = String(q.id).match(/(\d+)$/);
  return m ? m[1] : '';
};

/** 질의 노드만 훑는다(절 아래 서브쿼리 · UNION 갈래) */
function walkQueries(q, fn) {
  fn(q);
  q.clauses.forEach(c => c.children.forEach(ch => walkQueries(ch, fn)));
  q.branches.forEach(b => walkQueries(b, fn));
}

export function queryTitle(q, t) {
  if (q.kind === 'main') return t('common:sqb.kind.main');
  if (q.kind === 'cte') return q.label || t('common:sqb.kind.cte');
  if (q.kind === 'branch') return q.label || t('common:sqb.kind.branchFirst');
  return q.label || `${t(`common:sqb.kind.${q.kind}`)}#${ordinalOf(q)}`;
}

function ClauseSummary({ src, clause, t }) {
  const s = clauseSummary(src, clause);
  const kinds = Object.entries(s.kinds).filter(([k]) => k !== 'cte' && k !== 'insert')
    .map(([k, n]) => `${t(`common:sqb.kind.${k}`)} ${n}`).join(', ');
  let lead = null;
  if (clause.type === 'SELECT') lead = t('common:sqb.sum.cols', { count: s.count });
  else if (['WHERE', 'HAVING', 'CONNECT BY', 'START WITH'].includes(clause.type)) lead = t('common:sqb.sum.conds', { count: s.count });
  // SELECT 가 아닌 문장(INSERT·UPDATE …)은 첫머리만
  const list = clause.type === 'STATEMENT'
    ? src.slice(clause.start, clause.end).replace(/\s+/g, ' ').trim().slice(0, 48)
    : s.previews.join(', ') + (s.more ? ` ${t('common:sqb.sum.more', { count: s.more })}` : '');
  return (
    <span className="sqb-sum">
      {lead && <b>{lead}</b>}
      {kinds && <span className="sqb-sum-kinds">({kinds})</span>}
      {list && <span className="sqb-sum-list">{list}</span>}
    </span>
  );
}

/** 접힌 질의 한 줄 — 읽는 테이블을 먼저, 절 구성은 흐리게 뒤에 */
function CollapsedSummary({ q, src, t }) {
  const tables = queryTables(src, q);
  const shape = querySummary(q).map(([type, n]) => (type === 'BRANCHES' ? t('common:sqb.sum.branches', { count: n }) : `${type} ${n}`)).join(' · ');
  const list = tables.names.join(', ') + (tables.more ? ` ${t('common:sqb.sum.more', { count: tables.more })}` : '');
  return (
    <span className="sqb-sum sqb-sum-q" title={list ? `${list} — ${shape}` : shape}>
      {list && <span className="sqb-sum-tables">{list}</span>}
      <span className="sqb-sum-kinds">{shape}</span>
    </span>
  );
}

function AddMenu({ items, onPick, t, title }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <span className="sqb-addwrap" onClick={e => e.stopPropagation()}>
      <button type="button" className="sqb-icon" title={title} aria-label={title} aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <FiPlus size={12} />
      </button>
      {open && (
        <ul className="sqb-menu" role="menu" onMouseLeave={() => setOpen(false)}>
          {items.map(it => (
            <li key={it.key} role="menuitem" tabIndex={0}
              onClick={() => { setOpen(false); onPick(it); }}
              onKeyDown={e => { if (e.key === 'Enter') { setOpen(false); onPick(it); } }}>
              {it.label}
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}

function QueryCard({ q, src, parentClause, depth, ctx }) {
  const { t } = ctx;
  const collapsed = ctx.isCollapsed(q);
  const selected = ctx.selectedId === q.id;
  const queryAdds = [
    ...ADDABLE_CLAUSES.filter(type => !q.branches.length && q.clauses.length && q.clauses[0].type !== 'STATEMENT' && !q.clauses.some(c => c.type === type))
      .map(type => ({ key: `clause:${type}`, label: t('common:sqb.add.clause', { clause: type }), action: { op: 'addClause', type } })),
    ...(q.kind === 'main' || q.kind === 'cte' || q.kind === 'inline' ? [{ key: 'cte', label: t('common:sqb.add.cte'), action: { op: 'addCte' } }] : []),
    ...(q.clauses.some(c => c.type === 'SELECT') || q.branches.length ? [{ key: 'union', label: t('common:sqb.add.union'), action: { op: 'addUnion' } }] : []),
  ];
  const removable = depth > 0 && parentClause;
  return (
    <div className={`sqb-card sqb-k-${q.kind}${selected ? ' on' : ''}`} role="treeitem" aria-expanded={!collapsed} aria-selected={selected} data-sqb-id={q.id}>
      <div className="sqb-card-head" onClick={() => ctx.onSelect(q.id)}>
        <button type="button" className="sqb-chev" onClick={e => { e.stopPropagation(); ctx.onToggle(q.id); }}
          aria-label={collapsed ? t('common:sqb.expand') : t('common:sqb.collapse')}>
          {collapsed ? <FiChevronRight size={12} /> : <FiChevronDown size={12} />}
        </button>
        <span className="sqb-kind">{t(`common:sqb.kind.${q.kind}`)}</span>
        {q.kind !== 'main' && <span className="sqb-name">{queryTitle(q, t)}</span>}
        {collapsed && (
          <CollapsedSummary q={q} src={src} t={t} />
        )}
        <span className="sqb-acts">
          <AddMenu items={queryAdds} t={t} title={t('common:sqb.addTo', { name: queryTitle(q, t) })} onPick={it => ctx.onAction({ ...it.action, target: q.id })} />
          {removable && (
            <button type="button" className="sqb-icon" title={t('common:sqb.remove')} aria-label={t('common:sqb.remove')}
              onClick={e => { e.stopPropagation(); ctx.onAction({ op: 'remove', target: q.id }); }}>
              <FiX size={12} />
            </button>
          )}
        </span>
      </div>
      {!collapsed && (
        <div className="sqb-card-body">
          {q.clauses.map(c => <ClauseRow key={c.id} c={c} q={q} src={src} depth={depth} ctx={ctx} />)}
          {q.branches.map((b, i) => (
            <React.Fragment key={b.id}>
              {i > 0 && <div className="sqb-op">{q.ops[i - 1]}</div>}
              <QueryCard q={b} src={src} parentClause={null} depth={depth + 1} ctx={ctx} />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function ClauseRow({ c, q, src, depth, ctx }) {
  const { t } = ctx;
  const selected = ctx.selectedId === c.id;
  const adds = [];
  if (c.type === 'SELECT') adds.push({ key: 'scalar', label: t('common:sqb.add.scalar'), action: { op: 'addScalar' } });
  if (c.type === 'FROM') adds.push({ key: 'inline', label: t('common:sqb.add.inline'), action: { op: 'addInline' } });
  if (c.type === 'WHERE') {
    adds.push({ key: 'exists', label: t('common:sqb.add.exists'), action: { op: 'addCondition', kind: 'exists' } });
    adds.push({ key: 'in', label: t('common:sqb.add.in'), action: { op: 'addCondition', kind: 'in' } });
  }
  if (c.type === 'WITH') adds.push({ key: 'cte', label: t('common:sqb.add.cte'), action: { op: 'addCte', query: q.id } });
  return (
    <div className="sqb-clause-wrap">
      <div className={`sqb-clause${selected ? ' on' : ''}`} role="treeitem" aria-selected={selected} data-sqb-id={c.id} onClick={() => ctx.onSelect(c.id)}>
        <span className={`sqb-ctype sqb-c-${c.type.replace(/\s+/g, '-').toLowerCase()}`}>{c.type === 'STATEMENT' ? t('common:sqb.statement') : c.type}</span>
        <ClauseSummary src={src} clause={c} t={t} />
        <span className="sqb-acts">
          <AddMenu items={adds} t={t} title={t('common:sqb.addTo', { name: c.type })} onPick={it => ctx.onAction({ ...it.action, target: it.action.query || c.id })} />
          {c.type !== 'SELECT' && c.type !== 'STATEMENT' && (
            <button type="button" className="sqb-icon" title={t('common:sqb.remove')} aria-label={t('common:sqb.remove')}
              onClick={e => { e.stopPropagation(); ctx.onAction({ op: 'remove', target: c.id }); }}>
              <FiX size={12} />
            </button>
          )}
        </span>
      </div>
      {c.children.length > 0 && (
        <div className="sqb-children">
          {c.children.map(ch => <QueryCard key={ch.id} q={ch} src={src} parentClause={c} depth={depth + 1} ctx={ctx} />)}
        </div>
      )}
    </div>
  );
}

/** 처음부터 접어 두는 서브쿼리(2026-10-27 사용자 요청) — 조건·컬럼 하나짜리라 펼쳐 두면 바깥 질의의 모양이 안 보인다 */
const COLLAPSED_BY_DEFAULT = new Set(['scalar', 'exists', 'in']);

export default function SqlOutline({ root, src, selectedId, onSelect, onAction, dim }) {
  const { t } = useTranslation();
  // 사람이 바꾼 것만 든다 — 기본이 접힘인 종류는 펼친 것, 나머지는 접은 것. 다시 파싱해 새로 생긴 서브쿼리도 기본대로 선다
  const [flipped, setFlipped] = useState(() => new Set());
  const boxRef = useRef(null);
  const scrollReq = useRef(false);
  const kindOf = (id) => {
    let found = null;
    if (root) walkQueries(root, q => { if (q.id === id) found = q.kind; });
    return found;
  };
  const isCollapsedId = (id, kind) => (COLLAPSED_BY_DEFAULT.has(kind) ? !flipped.has(id) : flipped.has(id));
  // 고른 요소가 보이게 — 방금 넣은 요소가 아래에 생겨도 따라간다. 접힌 카드 안이면 펼친다.
  // **선택이 바뀔 때만** 한다: 접기에도 반응하면 고른 요소를 품은 카드를 접는 순간 도로 펼쳐지고, 그 큰 카드로 스크롤해
  // 화면이 맨 아래로 내려갔다(2026-10-27 신고 「접기를 눌렀는데 아래로 스크롤만 된다」)
  useEffect(() => {
    if (!selectedId) return;
    const parts = String(selectedId).split('/');
    const hidden = [];
    for (let i = 1; i < parts.length; i++) {
      const id = parts.slice(0, i).join('/');
      const kind = kindOf(id);
      if (kind && isCollapsedId(id, kind)) hidden.push(id);
    }
    scrollReq.current = true;
    if (hidden.length) setFlipped(prev => { const n = new Set(prev); hidden.forEach(id => { if (n.has(id)) n.delete(id); else n.add(id); }); return n; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);
  useEffect(() => {
    if (!scrollReq.current || !boxRef.current) return;
    scrollReq.current = false;
    const el = boxRef.current.querySelector(`[data-sqb-id="${CSS.escape(selectedId)}"]`);
    // 카드는 머리줄로 — 카드 전체를 맞추면 긴 카드에서 맨 아래로 간다
    const target = el && (el.querySelector(':scope > .sqb-card-head') || el);
    if (target && target.scrollIntoView) target.scrollIntoView({ block: 'nearest' });
  });
  if (!root) return <div className="sqb-empty">{t('common:sqb.empty')}</div>;
  const ctx = {
    t, selectedId, onSelect, onAction,
    isCollapsed: (q) => isCollapsedId(q.id, q.kind),
    onToggle: (id) => setFlipped(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }),
  };
  return (
    <div ref={boxRef} className={`sqb-outline${dim ? ' dim' : ''}`} role="tree" aria-label={t('common:sqb.outline')}>
      <QueryCard q={root} src={src} parentClause={null} depth={0} ctx={ctx} />
    </div>
  );
}
